import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useStore, useT } from "../store/useStore";
import { anmeldeUrsprung, appUrsprung, aufAnmeldeHost, aufAppHost } from "../lib/hosts";
import { Btn, Field, Icon, IconBtn, Input, Spinner, Toaster } from "../components/ui";
import { api, ApiError } from "../api/http";

// ============================================================
// Login + forgot password — ported from screen_login.jsx
// ============================================================

/**
 * Kartenbuehne (hell): abstrahierte Karte aus Rasterlinien, Wegen und
 * Flaechenpunkten — das Produktversprechen als Kulisse, ohne Foto.
 * Nachtglas (dunkel): leuchtende Ortsteil-Konturen auf fast Schwarz.
 * Beides reine Dekoration; aria-hidden, skaliert mit der Flaeche.
 */
function Buehne({ dark }: { dark: boolean }) {
  if (dark) {
    return (
      <svg aria-hidden viewBox="0 0 960 900" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
        <path d="M180,180 L420,150 L560,260 L520,430 L300,480 L170,360 Z" fill="none" stroke="#5fb0c4" strokeOpacity=".5" strokeWidth="1.5" />
        <path d="M560,260 L780,240 L860,420 L700,560 L520,430" fill="none" stroke="#5fb0c4" strokeOpacity=".28" strokeWidth="1.2" />
        <path d="M300,480 L360,660 L620,700 L700,560" fill="none" stroke="#5fb0c4" strokeOpacity=".2" strokeWidth="1.2" />
        <circle cx="420" cy="150" r="3.5" fill="#5fb0c4" /><circle cx="560" cy="260" r="3.5" fill="#5fb0c4" />
        <circle cx="520" cy="430" r="3.5" fill="#d9a441" /><circle cx="700" cy="560" r="3.5" fill="#5fb0c4" />
      </svg>
    );
  }
  const gitter: string[] = [];
  for (let a = 0; a <= 960; a += 80) gitter.push(`M${a},0 V900`);
  for (let a = 0; a <= 900; a += 80) gitter.push(`M0,${a} H960`);
  return (
    <svg aria-hidden viewBox="0 0 960 900" preserveAspectRatio="xMidYMid slice" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}>
      <path d={gitter.join(" ")} stroke="#014351" strokeOpacity=".06" fill="none" />
      <path d="M120,700 C260,560 300,620 430,520 S640,420 700,300 S840,180 940,160" fill="none" stroke="#014351" strokeOpacity=".26" strokeWidth="2.5" />
      <path d="M60,300 C180,340 260,240 380,280 S600,260 700,340" fill="none" stroke="#5fa8b8" strokeOpacity=".38" strokeWidth="2" />
      {([[190,220,7],[240,260,5],[300,180,6],[420,320,7],[520,240,5],[610,400,6],[360,470,7],[250,540,5],[540,560,6],[680,520,5]] as Array<[number,number,number]>).map(([x,y,r]) => (
        <circle key={`${x}-${y}`} cx={x} cy={y} r={r} fill="#c25a2b" />
      ))}
      <circle cx="720" cy="300" r="4" fill="#d9a441" /><circle cx="160" cy="420" r="4" fill="#d9a441" /><circle cx="470" cy="140" r="4" fill="#d9a441" />
    </svg>
  );
}

export function LoginScreen() {
  // Auf dem App-Host gibt es keine Anmeldung mehr — wer /login direkt
  // ansteuert, gehoert auf den Anmelde-Host.
  if (aufAppHost()) {
    location.href = `${anmeldeUrsprung()}/login`;
    return null;
  }
  const t = useT();
  const lang = useStore((s) => s.lang);
  const setLang = useStore((s) => s.setLang);
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const setAuth = useStore((s) => s.setAuth);
  const addToast = useStore((s) => s.addToast);
  const navigate = useNavigate();

  const [mode, setMode] = useState<"login" | "forgot" | "sent">("login");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotBusy, setForgotBusy] = useState(false);
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState(false);
  /** Text der Bremse (429) — sonst stuende hier faelschlich „Ungültige Anmeldedaten". */
  const [locked, setLocked] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(false);
    setLocked(null);
    void api
      .login({ identifier: user, password: pass })
      // Ohne diesen Zweig blieb der Knopf nach einer 429-Antwort ewig im
      // Spinner haengen: api.login faengt nur den 401 ab und wirft alles
      // andere weiter, und hier stand kein catch. Erst mit der neuen
      // Anmeldebremse ist dieser Fall ueberhaupt erreichbar.
      .catch((e: unknown) => {
        setBusy(false);
        if (e instanceof ApiError && e.status === 429) setLocked(e.message);
        else setErr(true);
        return undefined;
      })
      .then((res) => {
      if (res === undefined) return;
      setBusy(false);
      if (res) {
        setAuth(res);
        addToast({ type: "success", msg: lang === "de" ? "Erfolgreich angemeldet" : "Signed in" });
        // wer auf ein Startprojekt gepinnt ist, landet dort — unabhaengig von der
        // Rolle (die ist seit den Beschluessen vom 22.08. projektbezogen)
        // Auf dem Anmelde-Host (auth.*) lebt nur diese Seite — die Arbeit
        // wohnt auf app.*: harter Wechsel, das Sitzungscookie spannt per
        // SESSION_COOKIE_DOMAIN ueber beide.
        if (aufAnmeldeHost()) {
          location.href = `${appUrsprung()}${res.defaultProjectId ? `/project/${res.defaultProjectId}` : "/"}`;
          return;
        }
        if (res.defaultProjectId) navigate(`/project/${res.defaultProjectId}`);
        else navigate("/");
      } else setErr(true);
    });
  };

  return (
    <div style={{ minHeight: "100dvh", display: "grid", position: "relative" }} className="login-grid">
      {/* top controls */}
      <div style={{ position: "fixed", top: "calc(18px + var(--safe-top))", right: 20, zIndex: 10, display: "flex", gap: 8 }}>
        <IconBtn icon={theme === "dark" ? "sun" : "moon"} onClick={toggleTheme} title="Theme" />
        <div className="seg">
          <button className={lang === "de" ? "active" : ""} onClick={() => setLang("de")}>
            DE
          </button>
          <button className={lang === "en" ? "active" : ""} onClick={() => setLang("en")}>
            EN
          </button>
        </div>
      </div>

      {/* brand side */}
      <div
        className="login-brand"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "44px 48px",
          overflow: "hidden",
          background:
            theme === "dark"
              ? "radial-gradient(1200px 700px at 20% 20%, #0d2b32, var(--bg) 70%)"
              : "linear-gradient(145deg, #eef4f3, #e2edeb)",
        }}
      >
        <Buehne dark={theme === "dark"} />
        <button
          onClick={() => navigate("/")}
          title={lang === "de" ? "Zur Startseite" : "Back to home"}
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: theme === "dark" ? "#f4f1ef" : "var(--text)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            alignSelf: "flex-start",
          }}
        >
          <img src="/logo.svg" alt="Logo" style={{ height: 32, width: "auto", display: "block", filter: theme === "dark" ? "brightness(1.8) saturate(1.1)" : "none" }} />
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 11,
              background: theme === "dark" ? "rgba(255,255,255,0.16)" : "rgba(1,67,81,0.12)",
              color: theme === "dark" ? "#fff" : "var(--accent)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backdropFilter: "blur(6px)",
            }}
          >
            <Icon name="map-trifold" size={23} weight="fill" />
          </div>
          <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.02em" }}>SpielplatzGIS</span>
        </button>
        <div style={{ position: "relative", color: theme === "dark" ? "#f4f1ef" : "var(--text)" }}>
          <h1 style={{ fontSize: 38, lineHeight: 1.08, fontWeight: 600, maxWidth: 420 }}>
            {lang === "de" ? "Spielflächen, präzise im Blick." : "Play areas, precisely in view."}
          </h1>
          <p style={{ marginTop: 16, fontSize: 16, lineHeight: 1.55, color: theme === "dark" ? "rgba(255,255,255,0.82)" : "rgba(27,27,28,0.74)", maxWidth: 380 }}>
            {lang === "de"
              ? "Versorgungsanalyse, Mängelprüfung und Flächen­management für kommunale Spielplätze — auf einer Karte."
              : "Supply analysis, defect auditing and area management for municipal playgrounds — on one map."}
          </p>
          <div style={{ display: "flex", gap: 26, marginTop: 30 }}>
            {(
              [
                ["191", lang === "de" ? "Spielplätze" : "Playgrounds"],
                ["65k m²", lang === "de" ? "Fläche" : "Area"],
                ["3", "Kommunen"],
              ] as Array<[string, string]>
            ).map(([n, l]) => (
              <div key={l}>
                <div className="font-mono" style={{ fontSize: 24, fontWeight: 600, color: theme === "dark" ? "#fff" : "var(--accent)" }}>
                  {n}
                </div>
                <div style={{ fontSize: 12, color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(27,27,28,0.72)" }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ position: "relative", fontSize: 12, color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(27,27,28,0.72)" }}>© 2026 SpielplatzGIS</div>
      </div>

      {/* form side */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 24px", background: "var(--bg)" }}>
        <div style={{ width: "100%", maxWidth: 380 }} className="anim-up">
          {mode === "login" && (
            <>
              <h2 style={{ fontSize: 33, letterSpacing: "-0.02em" }}>{t("login.title")}</h2>
              <p style={{ color: "var(--text-mute)", marginTop: 7, marginBottom: 28 }}>{t("login.sub")}</p>
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Field label={t("login.user")}>
                  <Input
                    icon="user"
                    name="identifier"
                    autoComplete="username"
                    value={user}
                    onChange={(e) => {
                      setUser(e.target.value);
                      setErr(false);
                    }}
                    placeholder="s.schmidt"
                    autoFocus
                    style={{ fontSize: 16 }}
                  />
                </Field>
                <Field label={t("login.pass")}>
                  <div style={{ position: "relative" }}>
                    <Input
                      icon="lock-key"
                      name="password"
                      autoComplete="current-password"
                      type={show ? "text" : "password"}
                      value={pass}
                      onChange={(e) => {
                        setPass(e.target.value);
                        setErr(false);
                      }}
                      placeholder="••••••••"
                      style={{ fontSize: 16 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShow((s) => !s)}
                      style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "var(--text-mute)",
                        padding: 6,
                      }}
                    >
                      <Icon name={show ? "eye-slash" : "eye"} size={17} />
                    </button>
                  </div>
                </Field>
                {locked && (
                  <div className="anim-fade" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--warn)" }}>
                    <Icon name="clock-counter-clockwise" size={16} weight="fill" />
                    {locked}
                  </div>
                )}
                {err && !locked && (
                  <div className="anim-fade" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--error)" }}>
                    <Icon name="warning-circle" size={16} weight="fill" />
                    {lang === "de" ? "Ungültige Anmeldedaten." : "Invalid credentials."}
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: -4 }}>
                  <button
                    type="button"
                    onClick={() => { setLocked(null); setMode("forgot"); }}
                    style={{ fontSize: 13, fontWeight: 600, color: "var(--accent-text)", background: "none", border: "none", cursor: "pointer" }}
                  >
                    {t("login.forgot")}
                  </button>
                </div>
                <Btn type="submit" size="lg" disabled={busy}>
                  {busy ? <Spinner color="#fff" /> : <Icon name="sign-in" size={18} />}
                  {t("login.signin")}
                </Btn>
              </form>
            </>
          )}

          {mode === "forgot" && (
            <>
              <button
                onClick={() => { setLocked(null); setMode("login"); }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--text-mute)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  marginBottom: 22,
                }}
              >
                <Icon name="arrow-left" size={15} />
                {t("common.back")}
              </button>
              <h2 style={{ fontSize: 33, letterSpacing: "-0.02em" }}>{t("forgot.title")}</h2>
              <p style={{ color: "var(--text-mute)", marginTop: 7, marginBottom: 28 }}>{t("forgot.sub")}</p>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setLocked(null);
                  setForgotBusy(true);
                  void api
                    .forgotPassword(forgotEmail)
                    .then(() => setMode("sent"))
                    // Unbekannte Adressen sollen weiter wie ein Erfolg aussehen —
                    // eine BREMSE aber nicht: sonst glaubt jemand, die Mail sei
                    // unterwegs, und wartet auf etwas, das nie kommt.
                    .catch((e: unknown) => {
                      if (e instanceof ApiError && e.status === 429) setLocked(e.message);
                      else setMode("sent");
                    })
                    .finally(() => setForgotBusy(false));
                }}
                style={{ display: "flex", flexDirection: "column", gap: 16 }}
              >
                <Field label={t("acc.email")}>
                  <Input icon="envelope-simple" type="email" autoComplete="email" placeholder="name@kommune.de" required autoFocus value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} style={{ fontSize: 16 }} />
                </Field>
                {/* Die 429-Bremse galt bisher nur im Anmelde-Zweig — hier drehte
                    der Knopf stumm zurueck und niemand erfuhr, warum. */}
                {locked && (
                  <div className="anim-fade" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--warn)" }}>
                    <Icon name="clock-counter-clockwise" size={16} weight="fill" />
                    {locked}
                  </div>
                )}
                <Btn type="submit" size="lg" disabled={forgotBusy}>
                  {forgotBusy ? <Spinner color="#fff" /> : <Icon name="paper-plane-tilt" size={18} />}
                  {t("forgot.send")}
                </Btn>
              </form>
            </>
          )}

          {mode === "sent" && (
            <div className="anim-pop" style={{ textAlign: "center", padding: "20px 0" }}>
              <div
                style={{
                  width: 64,
                  height: 64,
                  borderRadius: 18,
                  background: "var(--accent-soft)",
                  color: "var(--accent-text)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                }}
              >
                <Icon name="envelope-open" size={32} weight="fill" />
              </div>
              <h2 style={{ fontSize: 23 }}>{t("forgot.sent")}</h2>
              <p style={{ color: "var(--text-mute)", marginTop: 10, marginBottom: 26 }}>
                {lang === "de"
                  ? "Folgen Sie dem Link in der E-Mail, um ein neues Passwort zu vergeben."
                  : "Follow the link in the email to set a new password."}
              </p>
              <Btn variant="ghost" onClick={() => { setLocked(null); setMode("login"); }}>
                <Icon name="arrow-left" size={16} />
                {lang === "de" ? "Zurück zur Anmeldung" : "Back to sign in"}
              </Btn>
            </div>
          )}
        </div>
      </div>

      <Toaster />
      <style>{`
        .login-grid{ grid-template-columns: 1fr; }
        /* Formularspalte als festes Mass (Karte 380px + Luft), die Buehne nimmt
           den Rest — auf 1440px ergibt das ~64/36, auf Ultrawide waechst nur
           die Kulisse, nie das Formular. */
        @media(min-width:920px){ .login-grid{ grid-template-columns: minmax(0,1fr) 520px; } }
        @media(max-width:919.98px){ .login-brand{ display:none!important; } }
      `}</style>
    </div>
  );
}
